import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { Currency } from '#src/__generated__/globalTypes.ts';
import { UserAccountEntryEntry } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { AccountEntry } from '#src/components/mySorare/TransactionsHistory/UserAccountEntries/AccountEntry/index.tsx';
import { getAccountIdentifier } from '#src/components/wallet/BankEthAccounting/Withdraw/WithdrawFiat/utils.ts';
import { isType } from '#src/lib/gql/index.ts';

import type { Withdrawal_userAccountEntry } from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Withdrawal',
  },
  titleFees: {
    defaultMessage: 'Withdrawal fees',
  },
  detail: {
    defaultMessage: 'Withdrawal to {address}',
  },
});

type WithdrawalType = NonNullable<
  Withdrawal_userAccountEntry['tokenOperation']
> & { __typename: 'Withdrawal' | 'FastWithdrawal' | 'MangopayWithdrawal' };

const isAToWithdrawal = function isAWithdrawal(item: {
  __typename: string;
  to?: string;
}): item is WithdrawalType {
  return (
    [
      'Withdrawal',
      'FastWithdrawal',
      'MangopayWithdrawal',
      'BankWithdrawal',
    ].includes(item?.__typename) && !!item.to
  );
};

const WithdrawalIcon = styled(FontAwesomeIcon)`
  width: var(--double-unit);
  height: var(--double-unit);
  border-radius: 50%;
  align-self: center;
  background-color: transparent;
  border: 1px solid var(--c-white);
  color: var(--c-white);
  padding: var(--unit);
`;

type Props = {
  userAccountEntry: Withdrawal_userAccountEntry;
  withIcon?: boolean;
  expandable?: boolean;
  primaryCurrency: Currency;
};

export const Withdrawal = ({
  userAccountEntry,
  withIcon,
  expandable,
  primaryCurrency,
}: Props) => {
  const { tokenOperation } = userAccountEntry;
  const { formatMessage } = useIntl();

  const destination = useMemo(() => {
    if (!tokenOperation || !isAToWithdrawal(tokenOperation)) return null;

    if (!isType(tokenOperation, 'MangopayWithdrawal')) {
      return tokenOperation.to;
    }

    return getAccountIdentifier(tokenOperation.bankAccount);
  }, [tokenOperation]);

  if (
    ![
      UserAccountEntryEntry.WITHDRAWAL,
      UserAccountEntryEntry.WITHDRAWAL_FEE,
    ].includesValue(userAccountEntry.entryType) ||
    !destination
  )
    return null;

  const transactionHash =
    userAccountEntry?.tokenOperation &&
    'transactionHash' in userAccountEntry.tokenOperation
      ? userAccountEntry.tokenOperation.transactionHash
      : null;

  return (
    <AccountEntry
      expandable={expandable}
      userAccountEntry={userAccountEntry}
      title={
        userAccountEntry.entryType === UserAccountEntryEntry.WITHDRAWAL_FEE
          ? formatMessage(messages.titleFees)
          : formatMessage(messages.title)
      }
      {...(withIcon && { icon: <WithdrawalIcon icon={faArrowUpRight} /> })}
      primaryCurrency={primaryCurrency}
    >
      <LabelM>
        <FormattedMessage
          {...messages.detail}
          values={{ address: destination }}
        />
      </LabelM>
      {transactionHash ? (
        <LabelM>
          <FormattedMessage
            defaultMessage="Transaction hash: {transactionHash}"
            values={{
              transactionHash,
            }}
          />
        </LabelM>
      ) : null}
    </AccountEntry>
  );
};

Withdrawal.fragments = {
  userAccountEntry: gql`
    fragment Withdrawal_userAccountEntry on UserAccountEntry {
      id
      entryType
      tokenOperation {
        ... on Node {
          id
        }
        ... on Withdrawal {
          id
          to
          transactionHash
        }
        ... on FastWithdrawal {
          id
          to
          transactionHash
        }
        ... on MangopayWithdrawal {
          id
          to
          bankAccount {
            ... on BankAccountInterface {
              id
            }
            ...getAccountIdentifier_bankAccount
          }
        }
        ... on BankWithdrawal {
          id
          to
          transactionHash
        }
      }
      ...AccountEntry_userAccountEntry
    }
    ${AccountEntry.fragments.userAccountEntry}
    ${getAccountIdentifier.fragments.bankAccount}
  ` as TypedDocumentNode<Withdrawal_userAccountEntry>,
};
